import {
	createFileRoute,
	Link,
	Outlet,
	useLocation,
} from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { fetchOrganizationByIdQueryOptions } from "@/api/organizations";
import { useApiParameters } from "@/hooks/useApiParameters";
import { OrganizationDetailsNavigation } from "@/routes/_auth/app/_app/organizations/-components/OrganizationDetailsNavigation";

export const Route = createFileRoute(
	"/_auth/app/_app/organizations/$organizationId",
)({
	loader: async ({ context, params }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		const { organizationId } = params;

		// Preload organization data
		context.queryClient.ensureQueryData(
			fetchOrganizationByIdQueryOptions(token, organizationId),
		);

		return { userId, token, organizationId };
	},
	component: OrganizationDetailsLayout,
});

function OrganizationDetailsLayout() {
	const { t } = useTranslation();
	const { token } = Route.useLoaderData();
	const { organizationId } = Route.useParams();
	const location = useLocation();

	// Determine active tab based on current route
	const getActiveTab = () => {
		const pathname = location.pathname;
		if (pathname.endsWith("/edit")) return "edit";
		return "overview";
	};

	return (
		<div className="flex flex-col h-full">
			{/* Page Header */}
			<div className="flex items-center gap-4 p-4 border-b border-base-300 bg-base-100">
				<Link
					to="/app/organizations"
					className="btn btn-ghost btn-sm"
					data-testid="organization-details-back-button"
				>
					<ArrowLeft className="h-4 w-4" />
					<span className="hidden sm:inline">{t("common.back")}</span>
				</Link>
				<div>
					<h1 className="text-lg font-semibold text-base-content">
						{t("organizations.details.title")}
					</h1>
					<p className="text-sm text-base-content/70">
						{t("organizations.details.description")}
					</p>
				</div>
			</div>

			{/* Main content area with side navigation */}
			<div
				className="flex-1 flex h-[calc(100vh-8rem)] min-h-0 overflow-hidden"
				data-testid="organization-details-container"
			>
				{/* Side Navigation */}
				<OrganizationDetailsNavigation
					activeTab={getActiveTab()}
					token={token}
					organizationId={organizationId}
				/>

				{/* Main Content Area */}
				<div
					className="flex-1 p-3 md:p-4 bg-base-200 min-h-0 overflow-auto"
					data-testid="organization-details-main-content-area"
				>
					<Outlet />
				</div>
			</div>
		</div>
	);
}
